/**
 * Role-based access control. Roles are per organization (Better Auth `member.role`).
 * Every API route declares the permission it needs; the matrix below is the single source of truth.
 */
export const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
export type Role = (typeof ROLES)[number];

export const RESOURCES = {
  agent: ['read', 'create', 'update', 'delete', 'run'],
  provider: ['read', 'create', 'update', 'delete'],
  secret: ['read', 'create', 'update', 'delete'],
  mcp: ['read', 'create', 'update', 'delete'],
  policy: ['read', 'update'],
  budget: ['read', 'update'],
  task: ['read', 'create', 'update', 'cancel'],
  run: ['read', 'cancel', 'resume'],
  approval: ['read', 'decide'],
  conversation: ['read', 'create'],
  usage: ['read'],
  trigger: ['read', 'create', 'update', 'delete'],
  notification: ['read', 'update'],
  member: ['read', 'invite', 'update', 'remove'],
  audit: ['read'],
  /** Daily run-error analysis: reports (read), manual run, schedule settings. */
  analysis: ['read', 'run', 'configure'],
  org: ['read', 'update', 'halt', 'delete'],
  /** Subscription changes and Stripe portal sessions are owner-only. */
  billing: ['manage'],
  /** Own personal access tokens (MCP / scripts). */
  token: ['read', 'manage'],
  /** Recording mode: recorded procedures (Chrome extension ingest). */
  recording: ['read', 'create', 'delete'],
  /** Executable procedures (synthesized from recordings); attaching to an agent needs agent:update. */
  flow: ['read', 'create', 'update', 'delete', 'run'],
  /** Skills steer agents, so authoring/activating is admin-level (members read). Activation is session-only. */
  skill: ['read', 'create', 'update', 'delete', 'activate'],
} as const;

export type Resource = keyof typeof RESOURCES;
export type Action<R extends Resource = Resource> = (typeof RESOURCES)[R][number];
export type Permission = { [R in Resource]: `${R}:${Action<R>}` }[Resource];

const READ_ALL = (Object.keys(RESOURCES) as Resource[])
  .filter((r) => (RESOURCES[r] as readonly string[]).includes('read'))
  .map((r) => `${r}:read` as Permission);

const MEMBER: Permission[] = [
  ...READ_ALL.filter((p) => p !== 'audit:read' && p !== 'secret:read'),
  'agent:run',
  'conversation:create',
  'task:create',
  'task:update',
  'task:cancel',
  'run:cancel',
  'run:resume',
  'approval:decide',
  'notification:update',
  'token:manage',
  'recording:create',
  'recording:delete',
  'flow:create',
  'flow:update',
  'flow:run',
];

const ADMIN: Permission[] = [
  ...MEMBER,
  'audit:read',
  'secret:read',
  'agent:create',
  'agent:update',
  'agent:delete',
  'provider:create',
  'provider:update',
  'provider:delete',
  'secret:create',
  'secret:update',
  'secret:delete',
  'mcp:create',
  'mcp:update',
  'mcp:delete',
  'policy:update',
  'budget:update',
  'trigger:create',
  'trigger:update',
  'trigger:delete',
  'flow:delete',
  'skill:create',
  'skill:update',
  'skill:delete',
  'skill:activate',
  'member:invite',
  /** Admins may change non-owner roles; promoting to / demoting from owner is owner-only. */
  'member:update',
  'org:halt',
  'analysis:run',
  'analysis:configure',
];

const OWNER: Permission[] = [...ADMIN, 'member:remove', 'org:update', 'org:delete', 'billing:manage'];

export const ROLE_PERMISSIONS: Record<Role, ReadonlySet<Permission>> = {
  owner: new Set(OWNER),
  admin: new Set(ADMIN),
  member: new Set(MEMBER),
  viewer: new Set(READ_ALL.filter((p) => p !== 'audit:read' && p !== 'secret:read')),
};

export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.has(permission) ?? false;
}

/** Roles that must have a second factor (TOTP or passkey) before privileged actions. */
export const ROLES_REQUIRING_2FA: ReadonlySet<Role> = new Set(['owner', 'admin']);
